import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext({
    favorites: new Set(),
    isFavorite: () => false,
    toggle: async () => {},
    ready: false,
});

export function FavoritesProvider({ children }) {
    const { user } = useAuth();
    const [favorites, setFavorites] = useState(() => new Set());
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let active = true;
        if (!user) {
            setFavorites(new Set());
            setReady(false);
            return;
        }
        api
            .get("/favorites")
            .then((r) => {
                if (active) {
                    setFavorites(new Set(r.data?.pes_ids || []));
                    setReady(true);
                }
            })
            .catch(() => {
                if (active) setReady(true);
            });
        return () => {
            active = false;
        };
    }, [user]);

    const isFavorite = useCallback((pesId) => favorites.has(pesId), [favorites]);

    const toggle = useCallback(async (pesId) => {
        if (pesId == null) return;
        // aggiornamento ottimistico
        setFavorites((prev) => {
            const next = new Set(prev);
            if (next.has(pesId)) next.delete(pesId);
            else next.add(pesId);
            return next;
        });
        try {
            const { data } = await api.post(`/favorites/${pesId}`);
            setFavorites(new Set(data?.pes_ids || []));
        } catch {
            // ripristino in caso di errore
            setFavorites((prev) => {
                const next = new Set(prev);
                if (next.has(pesId)) next.delete(pesId);
                else next.add(pesId);
                return next;
            });
        }
    }, []);

    return (
        <FavoritesContext.Provider value={{ favorites, isFavorite, toggle, ready }}>
            {children}
        </FavoritesContext.Provider>
    );
}

export const useFavorites = () => useContext(FavoritesContext);
